import "./App.css";
import { useEffect } from "react";
import SignIn from "./Components/SignIn/SignIn";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Moon, Sun } from "lucide-react";
import SignUp from "./Components/SignUp/SignUp";
import ForgotPassword from "./Components/AuthLayout/ForgotPassword";
import VerifyCode from "./Components/AuthLayout/VerifyCode";
import ResetPassword from "./Components/AuthLayout/ResetPassword";
import LoadingPage from "./Components/AuthLayout/LoadingPage";
import WelcomeSteps from "./Components/OnBoardingLayout/WelcomeSteps.jsx";
import ProfileSetup from "./Components/OnBoardingLayout/ProfileSetup.jsx";
import ExploreTracks from "./Components/OnBoardingLayout/ExploreTracks.jsx";
import Navbar from "./Components/Navbar/Navbar.jsx";
import TracksPage from "./Components/TraksPage/TracksPage.jsx";
import FeedbackPage from "./Components/TraksPage/FeedbackPage.jsx";
import Leaderboard from "./Components/TraksPage/Leaderboard.jsx";
import ProfilePage from "./Components/Profile/ProfilePage.jsx";
import Dashboard from "./Components/Dashboard/Dashboard.jsx";
import AssignmentSubmission from "./Components/Assignments/AssignmentSubmission.jsx";
import {
  AllAnnouncementsPage,
  AnalyticsDashboardPage,
  CourseBasicsPage,
  CourseCurriculumPage,
  CourseFinalPage,
  CoursePreviewPage,
  CourseSettingsPage,
  CreateAnnouncementPage,
  CreateTaskPage,
  EnrollmentRequestsPage,
  GlobalLeaderboardPage,
  GradingInterfacePage,
  InstructorNotificationCenterPage,
  StudentDocumentationPage,
  SuperAdminControlPanelPage,
  TasksPage,
  TasksSubmittedPage,
  TrackSocialSharePreviewPage,
} from "./Components/MissingPages/MissingPages.jsx";
import { RequireAuth, RequireGuest, RequireRoles } from "./Components/AuthLayout/RouteGuards";
import { useAuth } from "./context/AuthContext";
import { useTheme } from "./context/ThemeContext";
import GlobalLoadingOverlay from "./Components/Common/GlobalLoadingOverlay.jsx";
import SiteFooter from "./Components/Common/SiteFooter.jsx";
import { Analytics } from "@vercel/analytics/react";
import { INVALID_TOKEN_EVENT } from "./api/http";

function App() {
  const { isAuthenticated, clearAuthState } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const headerNavbarPages = ["/tracks", "/feedback", "/profile"];
  const noSidebarPages = ["/loading", "/welcome", "/step-2", "/step-3", ...headerNavbarPages];

  const showHeaderNavbar =
    isAuthenticated && headerNavbarPages.includes(location.pathname);
  const hasSidebarLayout =
    isAuthenticated && !noSidebarPages.includes(location.pathname);

  useEffect(() => {
    const handleInvalidToken = (event) => {
      clearAuthState();
      window.alert(
        event?.detail?.message ||
          "الجلسة انتهت أو الـ token غير صالح. من فضلك سجل الدخول مرة تانية."
      );
      navigate("/", { replace: true });
    };

    window.addEventListener(INVALID_TOKEN_EVENT, handleInvalidToken);
    return () => window.removeEventListener(INVALID_TOKEN_EVENT, handleInvalidToken);
  }, [clearAuthState, navigate]);

  return (
    <>
      {showHeaderNavbar ? <Navbar /> : null}
      <GlobalLoadingOverlay />
      {!isAuthenticated ? (
        <button
          type="button"
          className="theme-toggle-fab"
          onClick={toggleTheme}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>
      ) : null}
      <Routes>
        <Route element={<RequireGuest />}>
          <Route path="/" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify" element={<VerifyCode />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        <Route element={<RequireAuth />}>
          <Route path="/loading" element={<LoadingPage />} />
          <Route path="/welcome" element={<WelcomeSteps />} />
          <Route path="/step-2" element={<ProfileSetup />} />
          <Route path="/step-3" element={<ExploreTracks />} />
          <Route path="/tracks" element={<TracksPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/global-leaderboard" element={<GlobalLeaderboardPage />} />
          <Route path="/announcements" element={<AllAnnouncementsPage />} />
          <Route path="/student-guide" element={<StudentDocumentationPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/tasks" element={<TasksPage />} />

          <Route element={<RequireRoles allowedRoles={["student"]} />}>
            <Route path="/assignment" element={<AssignmentSubmission />} />
          </Route>

          <Route element={<RequireRoles allowedRoles={["instructor", "super_admin"]} />}>
            <Route path="/enrollment-requests" element={<EnrollmentRequestsPage />} />
            <Route path="/create-announcement" element={<CreateAnnouncementPage />} />
            <Route path="/create-task" element={<CreateTaskPage />} />
            <Route path="/analytics-dashboard" element={<AnalyticsDashboardPage />} />
            <Route path="/notification-center" element={<InstructorNotificationCenterPage />} />
            <Route path="/track-social-preview" element={<TrackSocialSharePreviewPage />} />
            <Route path="/tasks-submitted" element={<TasksSubmittedPage />} />
            <Route path="/grading-interface" element={<GradingInterfacePage />} />
            <Route path="/course-basics" element={<CourseBasicsPage />} />
            <Route path="/course-settings" element={<CourseSettingsPage />} />
            <Route path="/course-curriculum" element={<CourseCurriculumPage />} />
            <Route path="/course-preview" element={<CoursePreviewPage />} />
            <Route path="/course-final" element={<CourseFinalPage />} />
            <Route path="/grades" element={<GradingInterfacePage />} />
          </Route>

          <Route element={<RequireRoles allowedRoles={["super_admin"]} />}>
            <Route path="/super-admin" element={<SuperAdminControlPanelPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to={isAuthenticated ? "/tracks" : "/"} replace />} />
      </Routes>
      <SiteFooter offsetForSidebar={hasSidebarLayout} />
      <Analytics />
    </>
  );
}

export default App;
